/**
 * PKCE (Proof Key for Code Exchange) Utilities
 *
 * Implementation of OAuth 2.0 PKCE standard (RFC 7636)
 * Used for secure authorization code flow in public clients
 *
 * @see https://tools.ietf.org/html/rfc7636
 */

/**
 * Generate a code verifier for PKCE
 *
 * Creates a cryptographically random string of 64 characters
 * using URL-safe characters (A-Z, a-z, 0-9, -, ., _, ~)
 *
 * @returns A random 64-character string suitable for PKCE
 * @example
 * const verifier = generateCodeVerifier();
 * // => "dBjftJeZ4CVP-mB92K27uhbUJU1p1r_wW1gFWFOEjXk..."
 */
export const generateCodeVerifier = (): string => {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  let result = "";
  const charactersLength = characters.length;

  for (let i = 0; i < 64; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  return result;
};

/**
 * Generate a code challenge from a code verifier
 *
 * Creates a SHA-256 hash of the verifier and encodes it in base64url format
 * This is used in the authorization request to prove the client's identity
 *
 * @param codeVerifier - The code verifier to hash
 * @returns A promise that resolves to the base64url-encoded SHA-256 hash
 * @example
 * const verifier = generateCodeVerifier();
 * const challenge = await generateChallenge(verifier);
 * // => "E9Melhoa2OwvFrEMTJguCHaoeK1t8URWbuGJSstw-cM"
 */
export const generateChallenge = async (codeVerifier: string): Promise<string> => {
  // Hash the verifier using SHA-256
  const buffer = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(codeVerifier));

  // Convert to base64url format (RFC 4648 § 5)
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\//g, "_") // Replace / with _
    .replace(/\+/g, "-") // Replace + with -
    .replace(/=/g, ""); // Remove padding =
};
